/**
 * Work → Team (/team). People first: each teammate with their work, and the
 * AI employees that report to them nested underneath (what they do, their
 * schedule, what they're on, questions waiting for an answer, results, and a
 * box to give them more). AI employees live on the SyncSuite platform and
 * are made by chatting with SyncSuite AI; this page never creates them.
 */
import { useCallback, useEffect, useState } from "react";
import { Bot, MessageCircleQuestion, Sparkles } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Skeleton, useToast } from "@/components/ui";
import { Card, PageHeader } from "@/features/command/ui";
import { PersonCard } from "@/features/work/team/PersonCard";
import { AgentCard } from "@/features/work/team/AgentCard";
import { TaskDrawer, type TaskTarget } from "@/features/work/TaskDrawer";
import { useLookups, useMe } from "@/features/work/lib";
import type { Person, TeamData, WorkTask } from "@/features/work/types";

export function TeamPage() {
  const toast = useToast();
  const { wsTz, reload: reloadLookups } = useLookups();
  const { me } = useMe();
  const [team, setTeam] = useState<TeamData | null>(null);
  const [drawer, setDrawer] = useState<TaskTarget | null>(null);

  const load = useCallback(async () => {
    try {
      setTeam(await api.get<TeamData>("/team"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load the team");
      setTeam((t) => t ?? { ai_available: false, members: [], agents: [] });
    }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const changed = () => { void load(); void reloadLookups(); };
  const toggleTask = async (t: WorkTask) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === "completed" ? "pending" : "completed" });
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update that task");
    }
  };

  const people: Person[] = (team?.members ?? []).map((m) => ({ id: m.id, name: m.name, email: m.email, image: m.image }));
  const orphans = (team?.agents ?? []).filter((a) => !team?.members.some((m) => m.id === a.manager_id));
  const waiting = (team?.agents ?? []).reduce((n, a) => n + a.waiting_on_you.length, 0);

  return (
    <div className="space-y-4 pb-12">
      <PageHeader
        eyebrow="Work"
        title="Team"
        subtitle="Every job has a person who owns it. AI employees work under a person: you hand them work, answer their questions, and read what they did."
      />

      <Card className="flex items-start gap-3 p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/20">
          <Sparkles className="h-4 w-4 text-indigo-300" />
        </div>
        <div className="min-w-0 text-sm">
          <div className="font-medium text-zinc-100">New AI employees are made by chatting with SyncSuite AI</div>
          <div className="mt-0.5 text-xs leading-relaxed text-zinc-500">
            Say what you need ("make me an AI employee that finds podcasts to pitch every Monday"). It shows up here, and you pick who it reports to.
            {team && !team.ai_available && " AI employees appear here once this CRM is connected to SyncSuite."}
          </div>
        </div>
        {waiting > 0 && (
          <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium text-amber-200 ring-1 ring-inset ring-amber-400/25">
            <MessageCircleQuestion className="h-3.5 w-3.5" /> {waiting} waiting for an answer
          </span>
        )}
      </Card>

      {!team ? (
        <div className="grid gap-4 lg:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-72" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
            {team.members.map((m) => (
              <PersonCard
                key={m.id}
                m={m}
                agents={team.agents.filter((a) => a.manager_id === m.id)}
                team={team}
                people={people}
                wsTz={wsTz}
                isMe={me?.id === m.id}
                onChanged={changed}
                onOpenTask={(t) => setDrawer(t)}
                onToggleTask={(t) => void toggleTask(t)}
              />
            ))}
          </div>

          {orphans.length > 0 && (
            <Card className="p-4">
              <div className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                <Bot className="h-3.5 w-3.5 text-indigo-300" /> Not reporting to anyone yet
              </div>
              <p className="mb-3 text-xs text-zinc-500">Pick who each one reports to, so its work has an owner.</p>
              <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
                {orphans.map((a) => (
                  <AgentCard key={a.slug} agent={a} people={people} team={team} wsTz={wsTz} onChanged={changed} onOpenTask={(t) => setDrawer(t)} onToggleTask={(t) => void toggleTask(t)} />
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      <TaskDrawer target={drawer} onClose={() => setDrawer(null)} onChanged={() => void load()} />
    </div>
  );
}
